// ============================================================
// RECOMMENDATION ENGINE V1 (risk-first)
// Read-only over the FROZEN V2.1 scoring engine. Uses metricsFrom()
// as the impact estimator so projections can never contradict the score.
// ============================================================
import { metricsFrom, primitivesFromData } from "@/lib/calculations";

const M = 1000000;
const rp = (v) => "Rp" + String(Math.round((v / M) * 10) / 10).replace(".", ",") + " jt";
const mo = (x) => String(Math.round(x * 10) / 10).replace(".", ",");
// Display-consistent months (matches the rounded value shown to users).
const moDisp = (x) => Math.round(x * 10) / 10;
const emgPhrase = (emergency, emgM) =>
  emergency <= 0
    ? "Belum ada dana darurat yang tercatat"
    : `Dana darurat cukup untuk ${mo(emgM)} bulan pengeluaran wajib`;

// Risk ladder: lower = handled first (economic danger before score gain).
const RISK = {
  DEFICIT: 0, ZERO: 0, EXTREME_DEBT: 1, LIQUIDITY_CRIT: 2, DEBT_STRESS: 3,
  POSTSHOCK: 4, CONCENTRATION: 5, LIQUIDITY_BUILD: 6, DEBT_OPT: 7,
};

export function generateRecommendations(metrics, data) {
  const p = primitivesFromData(data);
  const m = metrics;
  const outflow = m.essentialOutflow, core = m.coreIncome, total = m.totalFamilyIncome;
  const emgM = m.emergencyMonths, d2c = m.debtToCoreIncome, dep = m.sideDependency, surplus = m.monthlyBalance;
  const largest = Math.max(p.husband, p.wife, p.sideTotal);
  const afterShock = total - largest;
  const covShock = outflow > 0 ? (afterShock + p.emergency / 12) / outflow : 1.5;
  const dualStable = (p.husband > 0 && p.wife > 0) || afterShock >= outflow;
  const proj = (mods) => metricsFrom({ ...p, ...mods }).score;

  if (total <= 0) {
    return {
      edge: "ZERO_INCOME", maintenance: false,
      recommendations: [{
        id: "zero", priority: "URGENT", lever: "INCOME", info: true,
        title: "Mulai catat & bangun pemasukan keluarga",
        current: "Belum ada pendapatan keluarga yang tercatat.",
        target: "Catat semua sumber pemasukan keluarga, sekecil apa pun.",
        why: "Tanpa pemasukan, ketahanan keluarga belum dapat dinilai.",
        action: "Mulai dari mencatat atau menstabilkan satu sumber pemasukan rutin.",
      }],
    };
  }

  const c = [];
  const vulnerable = !dualStable || dep > 30 || d2c > 40;

  if (surplus < 0) {
    const cut = Math.round((Math.abs(surplus) + 0.05 * outflow) / 5e5) * 5e5;
    c.push({ id: "deficit", risk: RISK.DEFICIT, lever: "SPEND", priority: "URGENT",
      title: "Tutup defisit bulanan",
      current: `Pengeluaran wajib melebihi total pemasukan sekitar ${rp(Math.abs(surplus))} per bulan.`,
      target: `Tekan pengeluaran wajib sekitar ${rp(cut)}/bulan, atau tambah pemasukan.`,
      why: "Selama pengeluaran lebih besar dari pemasukan, keluarga terus menggerus dana darurat setiap bulan.",
      action: "Tinjau pos pengeluaran rutin terbesar yang bisa ditekan sementara tanpa mengganggu kebutuhan pokok.",
      mods: { living: Math.max(0, p.living - cut) } });
  }
  if (core > 0 && d2c > 50 && p.totalDebt > 0) {
    const td = 0.4 * core;
    c.push({ id: "debt-extreme", risk: RISK.EXTREME_DEBT, lever: "DEBT", priority: "URGENT",
      title: "Kurangi tekanan cicilan",
      current: `Cicilan memakai ${Math.round(d2c)}% dari penghasilan utama keluarga — tergolong sangat tinggi.`,
      target: `Turunkan ke bawah 40% (kurangi cicilan sekitar ${rp(p.totalDebt - td)}/bulan).`,
      why: "Beban cicilan yang sangat tinggi membuat cashflow rapuh bila salah satu pemasukan berhenti.",
      action: "Percepat pelunasan utang berbunga tertinggi lebih dulu dan hindari menambah cicilan baru.",
      mods: { totalDebt: td } });
  }
  if (((emgM < 1 && vulnerable) || (emgM < 2 && afterShock <= 0)) && surplus >= 0) {
    const t = 3 * outflow;
    c.push({ id: "emg-crit", risk: RISK.LIQUIDITY_CRIT, lever: "EMG", priority: "URGENT",
      title: "Bangun dana darurat segera",
      current: `${p.emergency <= 0 ? "Belum ada dana darurat yang tercatat" : `Dana darurat hanya cukup untuk ${mo(emgM)} bulan`}, padahal pemasukan keluarga rentan terhadap guncangan.`,
      target: `Target awal 3 bulan pengeluaran wajib (${rp(t)}).`,
      why: "Tanpa bantalan minimal, satu gangguan pemasukan bisa langsung membuat keluarga kesulitan.",
      action: surplus > 0 ? `Sisihkan sebagian surplus (${rp(surplus)}/bln tersedia) ke rekening dana darurat terpisah.` : "Prioritaskan menyisihkan dana darurat begitu ada sisa bulanan.",
      mods: { emergency: t } });
  }
  if (core > 0 && d2c > 40 && d2c <= 50 && p.totalDebt > 0) {
    const td = 0.4 * core;
    c.push({ id: "debt-stress", risk: RISK.DEBT_STRESS, lever: "DEBT", priority: "IMPORTANT",
      title: "Kurangi tekanan cicilan",
      current: `Cicilan memakai ${Math.round(d2c)}% dari penghasilan utama keluarga.`,
      target: "Turunkan ke bawah 40%.",
      why: "Menurunkan beban cicilan memperkuat daya tahan bila terjadi guncangan pemasukan.",
      action: "Percepat pelunasan utang berbunga tertinggi dan tahan penambahan cicilan baru.",
      mods: { totalDebt: td } });
  }
  if (covShock < 1 && surplus >= 0) {
    const add = Math.round(Math.min(0.2 * total, Math.max(outflow - afterShock, 0.15 * total)) / 5e5) * 5e5;
    const tp = p.wife <= p.husband ? { wife: p.wife + add } : { husband: p.husband + add };
    c.push({ id: "postshock", risk: RISK.POSTSHOCK, lever: "DIV", priority: "IMPORTANT",
      title: "Perkuat kondisi bila pemasukan utama berhenti",
      current: `Jika pemasukan terbesar berhenti, sisa pemasukan hanya menutup ${Math.round(covShock * 100)}% pengeluaran wajib.`,
      target: `Bangun sumber pemasukan kedua yang lebih stabil (ilustrasi +${rp(add)}/bulan).`,
      why: "Sumber pemasukan kedua membuat keluarga tidak sepenuhnya bergantung pada satu penghasilan.",
      action: "Kembangkan keterampilan atau usaha yang bisa menjadi pemasukan rutin kedua secara bertahap.",
      mods: tp });
  }
  if (dep > 30 && covShock >= 1) {
    const add = Math.round((0.15 * total) / 5e5) * 5e5;
    const tp = p.wife <= p.husband ? { wife: p.wife + add } : { husband: p.husband + add };
    c.push({ id: "concentration", risk: RISK.CONCENTRATION, lever: "DIV", priority: dep > 60 ? "IMPORTANT" : "OPTIMIZATION",
      title: "Kurangi ketergantungan pada penghasilan tambahan",
      current: `Sekitar ${Math.round(dep)}% pemasukan berasal dari penghasilan tambahan yang cenderung tidak tetap.`,
      target: `Perkuat pemasukan utama (ilustrasi +${rp(add)}/bulan).`,
      why: "Mengurangi ketergantungan pada pemasukan yang tidak tetap membuat kondisi keluarga lebih stabil.",
      action: "Fokus menumbuhkan pemasukan utama yang lebih rutin.",
      mods: tp });
  }
  if (emgM < 6 && surplus >= 0 && !((emgM < 1 && vulnerable) || (emgM < 2 && afterShock <= 0))) {
    const first = moDisp(emgM) < 3 ? 3 : 6, t = first * outflow;
    c.push({ id: "emg-build", risk: RISK.LIQUIDITY_BUILD, lever: "EMG", priority: moDisp(emgM) < 3 ? "IMPORTANT" : "OPTIMIZATION",
      title: "Tambah dana darurat",
      current: `${emgPhrase(p.emergency, emgM)}.`,
      target: first === 6
        ? `Target 6 bulan pengeluaran wajib (${rp(t)}).`
        : `Target awal ${first} bulan (${rp(t)}), ideal 6 bulan.`,
      why: "Dana darurat 6 bulan memberi ruang bernapas bila pemasukan terganggu sementara.",
      action: surplus > 0 ? `Sisihkan sebagian surplus (${rp(surplus)}/bln) ke rekening dana darurat terpisah.` : "Sisihkan rutin begitu ada sisa bulanan.",
      mods: { emergency: t } });
  }
  if (core > 0 && d2c > 20 && d2c <= 40 && p.totalDebt > 0) {
    const td = 0.2 * core;
    c.push({ id: "debt-opt", risk: RISK.DEBT_OPT, lever: "DEBT", priority: "OPTIMIZATION",
      title: "Rapikan cicilan (opsional)",
      current: `Cicilan memakai ${Math.round(d2c)}% penghasilan utama — masih dalam batas wajar.`,
      target: "Bila memungkinkan, turunkan menuju 20%.",
      why: "Menurunkan cicilan lebih jauh menambah ruang cashflow keluarga.",
      action: "Alokasikan kelebihan dana untuk mempercepat pelunasan cicilan.",
      mods: { totalDebt: td } });
  }

  // One candidate per lever (keep highest-priority / lowest-risk instance).
  const byLever = {};
  for (const cand of c) if (!byLever[cand.lever] || cand.risk < byLever[cand.lever].risk) byLever[cand.lever] = cand;
  const list = Object.values(byLever);
  list.forEach((x) => { x.delta = proj(x.mods) - m.score; });
  list.sort((a, b) => a.risk - b.risk || b.delta - a.delta);
  const top = list.slice(0, 3);

  if (top.length === 0) return { maintenance: true, edge: null, recommendations: [] };

  // Cumulative projection through the frozen engine.
  let cur = { ...p }, from = m.score;
  const recommendations = top.map((x) => {
    cur = { ...cur, ...x.mods };
    const to = metricsFrom(cur).score;
    const r = {
      id: x.id, priority: x.priority, lever: x.lever, title: x.title,
      current: x.current, target: x.target, why: x.why, action: x.action,
      scoreFrom: from, scoreTo: to, delta: to - from,
    };
    from = to;
    return r;
  });
  return { maintenance: false, edge: null, recommendations };
}
